import React, { useCallback, useDeferredValue, useEffect, useRef, useState } from 'react';
import './ThreeDEarthSection.css';
import GeminiOnlyEarth from './GeminiOnlyEarth';
import { Button } from './ui/button';
import { Input } from './ui/input';
import AIRPORT_SEARCH_INDEX_PATH, {
  getAirportDisplayCode,
  getAirportLocationLabel,
  getAirportLookupCode,
  normalizeAirportQuery,
  searchAirportIndex,
} from '../lib/airportSearch';

const buildAirportSummary = (airport) => {
  const codes = [airport?.iata, airport?.icao]
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(' / ');
  const location = getAirportLocationLabel(airport);

  return [codes || getAirportDisplayCode(airport), airport?.name, location]
    .filter(Boolean)
    .join(' · ');
};

const SEARCH_RESULTS_LIMIT = 12;
const SEARCH_RESULTS_VISIBLE_COUNT = 4;

export default function ThreeDEarthSection() {
  const earthRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchFieldRef = useRef(null);
  const searchPanelRef = useRef(null);
  const isComposingRef = useRef(false);
  const ignoreNextSubmitRef = useRef(false);
  const [earthReady, setEarthReady] = useState(false);
  const [gridVisible, setGridVisible] = useState(false);
  const [airportQuery, setAirportQuery] = useState('');
  const [airportIndex, setAirportIndex] = useState([]);
  const [searchIndexState, setSearchIndexState] = useState('loading');
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [resultsPanelOpen, setResultsPanelOpen] = useState(false);
  const [highlightedResultIndex, setHighlightedResultIndex] = useState(0);
  const [searchPanelCanScrollDown, setSearchPanelCanScrollDown] = useState(false);
  const [status, setStatus] = useState({
    tone: 'hint',
    text: '输入机场名称、IATA 或 ICAO，系统会自动匹配真实坐标，并在 3D Earth 中生成可点击的机场 POI。',
  });

  const normalizedQuery = normalizeAirportQuery(airportQuery);
  const deferredQuery = useDeferredValue(airportQuery);
  const normalizedDeferredQuery = normalizeAirportQuery(deferredQuery);
  const searchResults = searchAirportIndex(deferredQuery, airportIndex, SEARCH_RESULTS_LIMIT);
  const canRenderResultsPanel = Boolean(normalizedQuery) && searchIndexState === 'ready';
  const shouldShowResults = resultsPanelOpen && canRenderResultsPanel;
  const resultsPanelMounted = searchIndexState === 'ready';
  const shouldShowScrollCue =
    shouldShowResults &&
    searchResults.length > SEARCH_RESULTS_VISIBLE_COUNT &&
    searchPanelCanScrollDown;

  useEffect(() => {
    const controller = new AbortController();

    const loadAirportIndex = async () => {
      setSearchIndexState('loading');

      try {
        const response = await fetch(AIRPORT_SEARCH_INDEX_PATH, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load airport index: ${response.status}`);
        }

        const data = await response.json();
        setAirportIndex(Array.isArray(data) ? data : []);
        setSearchIndexState('ready');
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        console.error('机场搜索索引加载失败:', error);
        setAirportIndex([]);
        setSearchIndexState('error');
      }
    };

    loadAirportIndex();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    earthRef.current?.setGridVisibility(gridVisible);
  }, [gridVisible]);

  useEffect(() => {
    setHighlightedResultIndex(0);
  }, [normalizedDeferredQuery]);

  useEffect(() => {
    if (!searchResults.length) {
      setHighlightedResultIndex(0);
      return;
    }

    if (highlightedResultIndex >= searchResults.length) {
      setHighlightedResultIndex(searchResults.length - 1);
    }
  }, [highlightedResultIndex, searchResults.length]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (searchFieldRef.current?.contains(event.target)) {
        return;
      }

      setResultsPanelOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, []);

  const updateSearchPanelScrollState = useCallback(() => {
    const searchPanelNode = searchPanelRef.current;

    if (!searchPanelNode || !shouldShowResults || searchResults.length <= SEARCH_RESULTS_VISIBLE_COUNT) {
      setSearchPanelCanScrollDown(false);
      return;
    }

    const nextCanScrollDown =
      searchPanelNode.scrollTop + searchPanelNode.clientHeight < searchPanelNode.scrollHeight - 2;
    setSearchPanelCanScrollDown(nextCanScrollDown);
  }, [searchResults.length, shouldShowResults]);

  useEffect(() => {
    if (!resultsPanelMounted) {
      setSearchPanelCanScrollDown(false);
      return undefined;
    }

    const frameId = window.requestAnimationFrame(updateSearchPanelScrollState);
    const handleResize = () => {
      updateSearchPanelScrollState();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleResize);
    };
  }, [
    resultsPanelMounted,
    searchResults.length,
    shouldShowResults,
    updateSearchPanelScrollState,
  ]);

  useEffect(() => {
    if (!shouldShowResults || !searchResults.length || !searchPanelRef.current) {
      return;
    }

    const activeIndex = Math.min(
      highlightedResultIndex,
      Math.max(searchResults.length - 1, 0)
    );
    const activeNode = searchPanelRef.current.querySelector(
      `#earth-airport-option-${activeIndex}`
    );

    activeNode?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    });
  }, [highlightedResultIndex, searchResults, shouldShowResults]);

  const locateAirport = (airport, source = 'manual') => {
    const summary = buildAirportSummary(airport);
    const airportPayload = {
      ...airport,
      code: getAirportDisplayCode(airport),
      lookupCode: getAirportLookupCode(airport),
      poiSource: 'search',
    };

    setSelectedAirport(airportPayload);
    setAirportQuery(`${airportPayload.code} ${airportPayload.name}`);
    setResultsPanelOpen(false);
    searchInputRef.current?.blur();

    const dispatchResult = earthRef.current
      ? earthRef.current.locateAirport(airportPayload, {
          focusCamera: true,
          showMarker: true,
          openOverlay: false,
        })
      : { queued: true };

    setStatus({
      tone: dispatchResult?.queued ? 'pending' : 'success',
      text: dispatchResult?.queued
        ? `已锁定 ${summary}，3D Earth 场景准备完成后会自动生成 POI。`
        : source === 'auto'
          ? `已自动匹配 ${summary}，3D Earth 中已生成可点击机场 POI。`
          : `已定位到 ${summary}，现在可以点击地球上的生成 POI 进入机场详情。`,
    });
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    if (isComposingRef.current || ignoreNextSubmitRef.current) {
      ignoreNextSubmitRef.current = false;
      return;
    }

    searchInputRef.current?.blur();

    const query = airportQuery.trim();
    if (!query) {
      setStatus({
        tone: 'error',
        text: '请先输入机场名称、IATA 或 ICAO 关键字。',
      });
      return;
    }

    if (searchIndexState === 'loading') {
      setStatus({
        tone: 'pending',
        text: '机场搜索索引仍在加载，请稍候再试。',
      });
      return;
    }

    if (searchIndexState === 'error') {
      setStatus({
        tone: 'error',
        text: '机场搜索索引加载失败，当前无法完成机场匹配。',
      });
      return;
    }

    if (!searchResults.length) {
      setStatus({
        tone: 'error',
        text: `没有找到与 “${query}” 匹配的机场，请尝试更完整的名称、IATA 或 ICAO。`,
      });
      setResultsPanelOpen(true);
      return;
    }

    locateAirport(searchResults[0], 'auto');
  };

  const handleAirportQueryChange = (event) => {
    setAirportQuery(event.target.value);
    setResultsPanelOpen(true);
  };

  const handleAirportQueryFocus = () => {
    if (airportQuery.trim()) {
      setResultsPanelOpen(true);
    }
  };

  const handleAirportQueryClick = (event) => {
    if (event.currentTarget.value.length > 0) {
      event.currentTarget.select();
    }
  };

  const handleAirportQueryCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handleAirportQueryCompositionEnd = () => {
    isComposingRef.current = false;
  };

  const handleAirportQueryKeyDown = (event) => {
    const isImeComposing =
      isComposingRef.current ||
      event.nativeEvent?.isComposing ||
      event.keyCode === 229;

    if (isImeComposing) {
      if (event.key === 'Enter') {
        ignoreNextSubmitRef.current = true;
      }
      return;
    }

    if (!searchResults.length) {
      if (event.key === 'Escape') {
        setResultsPanelOpen(false);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setResultsPanelOpen(true);
      setHighlightedResultIndex((currentIndex) =>
        (currentIndex + 1) % searchResults.length
      );
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setResultsPanelOpen(true);
      setHighlightedResultIndex((currentIndex) =>
        (currentIndex - 1 + searchResults.length) % searchResults.length
      );
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setResultsPanelOpen(false);
      return;
    }

    if (event.key === 'Enter' && shouldShowResults) {
      event.preventDefault();
      const airport =
        searchResults[
          Math.min(highlightedResultIndex, Math.max(searchResults.length - 1, 0))
        ];
      if (airport) {
        locateAirport(airport, 'manual');
      }
    }
  };

  const handleResultPointerDown = (event) => {
    event.preventDefault();
  };

  const handleClear = () => {
    earthRef.current?.clearAirportFocus();
    setSelectedAirport(null);
    setAirportQuery('');
    setResultsPanelOpen(false);
    setStatus({
      tone: 'hint',
      text: '已清除当前搜索机场 POI，你可以继续输入新的机场关键字。',
    });
  };

  const handleGridVisibilityChange = (event) => {
    setGridVisible(event.target.checked);
  };

  const selectedAirportLocation = getAirportLocationLabel(selectedAirport);
  const selectedAirportCodes = Array.from(
    new Set([selectedAirport?.code, selectedAirport?.icao].filter(Boolean))
  ).join(' / ');

  return (
    <section className="three-d-earth-section" aria-labelledby="three-d-earth-title">
      <div className="three-d-earth-shell">
        <div className="three-d-earth-intro">
          <div className="three-d-earth-copy">
            <span className="three-d-earth-eyebrow">3D Earth</span>
            <h2 id="three-d-earth-title" className="three-d-earth-title">
              Earth Live Module
            </h2>
          </div>
          <p className="three-d-earth-description">
            输入机场关键字后，系统会自动匹配机场数据库里的真实坐标，并在 3D Earth 中生成一个可点击进入 detail dialog 的动态机场 POI。
          </p>
          <div className="three-d-earth-meta" aria-label="Earth interaction hints">
            <span className="three-d-earth-meta-item">机场名 / IATA / ICAO</span>
            <span className="three-d-earth-meta-item">真实坐标自动定位</span>
            <span className="three-d-earth-meta-item">点击生成 POI</span>
            <span className="three-d-earth-meta-item">Live day-night</span>
          </div>
        </div>

        <div className="three-d-earth-stage-shell">
          <div className="three-d-earth-stage">
            <GeminiOnlyEarth ref={earthRef} onReadyChange={setEarthReady} />
          </div>

          <form className="three-d-earth-controls" onSubmit={handleSearchSubmit}>
            <div className="three-d-earth-toolbar">
              <div ref={searchFieldRef} className="three-d-earth-field three-d-earth-field--search">
                <div className="three-d-earth-field-heading">
                  <label className="three-d-earth-field-label" htmlFor="earth-airport-search">
                    机场搜索
                  </label>
                  <span className="three-d-earth-field-hint">name / city / IATA / ICAO</span>
                </div>
                <Input
                  ref={searchInputRef}
                  id="earth-airport-search"
                  className="three-d-earth-input"
                  inputMode="search"
                  placeholder="例如 Shanghai Pudong / PVG / ZSPD / Muscat / MCT / OOMS"
                  value={airportQuery}
                  onChange={handleAirportQueryChange}
                  onFocus={handleAirportQueryFocus}
                  onClick={handleAirportQueryClick}
                  onCompositionStart={handleAirportQueryCompositionStart}
                  onCompositionEnd={handleAirportQueryCompositionEnd}
                  onKeyDown={handleAirportQueryKeyDown}
                  aria-expanded={shouldShowResults}
                  aria-controls="earth-airport-search-results"
                  aria-activedescendant={
                    shouldShowResults && searchResults[highlightedResultIndex]
                      ? `earth-airport-option-${highlightedResultIndex}`
                      : undefined
                  }
                  aria-label="Search airport by name, IATA, or ICAO"
                />

                {resultsPanelMounted ? (
                  <div
                    className="three-d-earth-search-panel-shell"
                    data-open={shouldShowResults ? 'true' : 'false'}
                    aria-hidden={!shouldShowResults}
                  >
                    <div className="three-d-earth-search-panel">
                      <div
                        id="earth-airport-search-results"
                        ref={searchPanelRef}
                        className="three-d-earth-search-panel-scroll"
                        aria-label="机场匹配结果"
                        role="listbox"
                        onScroll={updateSearchPanelScrollState}
                      >
                        {searchResults.length ? (
                          searchResults.map((airport, airportIndex) => {
                            const airportLocation = getAirportLocationLabel(airport);
                            const airportCodes = [airport.iata, airport.icao]
                              .filter(Boolean)
                              .filter((value, index, values) => values.indexOf(value) === index);
                            const isHighlighted = airportIndex === highlightedResultIndex;

                            return (
                              <button
                                key={`${airport.lookupCode || airport.code}-${airport.lat}-${airport.lon}`}
                                id={`earth-airport-option-${airportIndex}`}
                                type="button"
                                className={`three-d-earth-search-result${
                                  isHighlighted ? ' three-d-earth-search-result--active' : ''
                                }`}
                                onPointerDown={handleResultPointerDown}
                                onMouseEnter={() => setHighlightedResultIndex(airportIndex)}
                                onClick={() => locateAirport(airport, 'manual')}
                                role="option"
                                aria-selected={isHighlighted}
                              >
                                <span className="three-d-earth-search-result-head">
                                  <span className="three-d-earth-search-result-name">
                                    {airport.name}
                                  </span>
                                  <span className="three-d-earth-search-result-codes">
                                    {airportCodes.length
                                      ? airportCodes.join(' / ')
                                      : getAirportDisplayCode(airport)}
                                  </span>
                                </span>
                                <span className="three-d-earth-search-result-meta">
                                  {airportLocation || '位置待补充'}
                                </span>
                              </button>
                            );
                          })
                        ) : (
                          <div className="three-d-earth-search-empty">
                            没有找到匹配机场，请尝试使用更完整的机场名、IATA 或 ICAO。
                          </div>
                        )}
                      </div>

                      <div
                        className="three-d-earth-search-scroll-cue"
                        data-visible={shouldShowScrollCue ? 'true' : 'false'}
                        aria-hidden="true"
                      >
                        <span>⬇ 更多结果</span>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="three-d-earth-actions">
                <Button className="three-d-earth-action three-d-earth-action--primary" type="submit">
                  搜索并定位机场
                </Button>

                <Button
                  className="three-d-earth-action three-d-earth-action--secondary"
                  type="button"
                  variant="outline"
                  onClick={handleClear}
                >
                  清除搜索 POI
                </Button>
              </div>
            </div>

            <div className="three-d-earth-controls-footer">
              <label className="three-d-earth-toggle">
                <input
                  type="checkbox"
                  checked={gridVisible}
                  onChange={handleGridVisibilityChange}
                />
                <span>显示经纬线</span>
              </label>

              <div className="three-d-earth-status-wrap">
                <p className="three-d-earth-note">
                  支持机场名称、城市、IATA、ICAO，例如 PVG / ZSPD / Shanghai Pudong / Muscat / MCT / OOMS
                </p>

                {selectedAirport ? (
                  <p className="three-d-earth-selection">
                    <span className="three-d-earth-selection-label">当前锁定</span>
                    <strong>{selectedAirport.name}</strong>
                    <span>{selectedAirportCodes}</span>
                    {selectedAirportLocation ? <span>{selectedAirportLocation}</span> : null}
                  </p>
                ) : null}

                <p
                  className={`three-d-earth-status three-d-earth-status--${status.tone}`}
                  role="status"
                >
                  {searchIndexState === 'loading'
                    ? '机场搜索索引加载中，稍后即可开始搜索。'
                    : searchIndexState === 'error'
                      ? '机场搜索索引加载失败，当前搜索功能不可用。'
                      : earthReady
                        ? status.text
                        : `${status.text} 地球场景仍在加载时，请求会自动排队。`}
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
